import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageForm from './PageForm';
import { Typography, Layout, Button, Row, Col, Modal, Input, Icon, message, Spin } from 'antd';
import { createPage } from '../../../actions/pages.actions';
import { fetchSinglePage, clearSinglePage } from '../../../actions/singlePage.actions';
import './Pages.css';
import PageCard from './PageCard';
import PagesTree from './PagesTree';
import { isEmpty } from 'lodash';
import stringSimilarity from 'string-similarity';

const { Header, Content, Footer } = Layout;
const { Text, Title } = Typography;

const Pages = props => {
    const { page, pages } = props;
    const { fetchSinglePage, clearSinglePage } = props;
    const { isDeleting, deleteSuccess, deleteError } = props;
    const { isCreating, createSuccess, createError } = props;

    const [createPageModalVisible, setCreatePageModalVisible] = useState(false);
    const [pagesFilter, setPagesFilter] = useState('');
    const [activePage, setActivePage] = useState(null);
    const [loading, setLoading] = useState(false);

    const getPagePathFromURL = () => {
        let urlArr = props.location.pathname.split('/');
        urlArr.splice(0, 2);

        urlArr = urlArr.filter(part => part !== '');

        return urlArr;
    };

    const goToParentPage = () => {
        const urlPagePath = getPagePathFromURL();
        urlPagePath.pop();

        props.history.push('/pages/' + urlPagePath.join('/'));
        setPagesFilter('');
    };

    /** When: URL path changed **/
    useEffect(() => {
        const urlPagePath = getPagePathFromURL();
        if (urlPagePath.length !== 0) {
            fetchSinglePage(urlPagePath);
        } else {
            setActivePage(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.location.pathname, fetchSinglePage]);

    /** When: Single page fetched **/
    useEffect(() => {
        if (!isEmpty(page)) {
            if (page.found) {
                setActivePage(page);
            } else {
                setActivePage(null);
            }
        } else {
            setActivePage(null);
        }
    }, [page]);

    useEffect(() => {
        return () => {
            clearSinglePage();
        };
    }, [clearSinglePage]);

    /** When: page is being created, creation failed or succeeded. **/
    useEffect(() => {
        if (isCreating === false) {
            if (createError) {
                message.error(createError);
                setLoading(false);
            } else {
                setCreatePageModalVisible(false);
                if (createSuccess) {
                    message.success('Page successfully created.');
                    goToParentPage();
                    setLoading(false);
                }
            }
        } else if (isCreating === true) {
            setLoading(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreating, createSuccess, createError]);

    /** When: page is being deleted, deletion failed or succeeded. **/
    useEffect(() => {
        if (isDeleting === false) {
            if (deleteError) {
                message.error(deleteError);
                setLoading(false);
            } else {
                if (deleteSuccess) {
                    message.success('Page successfully deleted.');
                    setLoading(false);
                    goToParentPage();
                }
            }
        } else if (isDeleting === true) {
            setLoading(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDeleting, deleteSuccess, deleteError]);

    /* ~~~~ Util-methods ~~~~ */

    const createPageCards = () => {
        let visiblePages = !activePage || isEmpty(activePage) ? pages : Object.values(activePage.subpages);

        return applyPagesFilter(visiblePages, pagesFilter).sort( (a, b) => (a.order !== undefined ? a.order : Number.MAX_VALUE) - (b.order !== undefined? b.order : Number.MAX_VALUE)).map(createPageCard);
    };
    const createPageCard = pageToCreate => {
        return <PageCard page={pageToCreate} key={pageToCreate.path} onClick={handlePageCardClicked} />;
    };
    const applyPagesFilter = (pages, pagesFilter) => {
        if (pagesFilter.trim().length > 0) {
            let pageFilterKeywords = pagesFilter.split(' ');

            pages = pages.filter(currentPage => {
                let pageTitleKeywords = currentPage.title.split(' ');
                return (
                    undefined !==
                    pageFilterKeywords.find(pageFilterKeyword => {
                        for (let index in pageTitleKeywords) {
                            if (stringSimilarity.compareTwoStrings(pageTitleKeywords[index].toUpperCase(), pageFilterKeyword.toUpperCase()) >= 0.5) {
                                return true;
                            }
                        }
                        return false;
                    })
                );
            });
        }
        return pages;
    };
    const navigateToPage = pagePath => {
        props.history.push('/pages' + pagePath);
        setPagesFilter('');
    };

    /* ~~~~ Event-handlers ~~~~ */

    const handleCreatePageClicked = () => {
        setCreatePageModalVisible(true);
    };
    const handleCreatePageOk = data => {
        props.createPage(data);
    };
    const handleCreatePageCancel = () => {
        setCreatePageModalVisible(false);
    };
    const handlePageCardClicked = clickedPage => {
        if (!isEmpty(clickedPage.subpages)) {
            navigateToPage(clickedPage.path);
        } else {
            message.info("This page doesn't have any sub pages.");
        }
    };
    const handleReturnClicked = clickedPage => {
        goToParentPage();
    };
    const handleSearchPagesInputChanged = e => {
        setPagesFilter(e.target.value);
    };

    return (
        <React.Fragment>
            <Layout className="layout">
                <Header className="header" style={{ background: 'none', padding: 0 }}>
                    <Row type="flex" justify="space-between">
                        <Col span={18}>
                            <Title level={1} className="pageTitle">
                                Pages
                            </Title>
                            <Text type="secondary" className="pageTitle--subscript">
                                {"showing children of : ' " + (activePage ? activePage.path : '/') + " '"}
                            </Text>
                        </Col>
                        <Col span={6}>
                            <Row type="flex" justify="end" gutter={12}>
                                <Col>
                                    <Button type="default" icon="rollback" onClick={handleReturnClicked} style={!activePage ? { display: 'none' } : {}}>
                                        Return
                                    </Button>
                                </Col>
                                <Col>
                                    <Input
                                        onChange={handleSearchPagesInputChanged}
                                        value={pagesFilter}
                                        placeholder="Filter pages"
                                        suffix={<Icon type="search" style={{ color: 'rgba(0,0,0,.45)' }} />}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Header>
                <Content className="content pagesOverview-conent">
                    <div className="content-sider">
                        <PagesTree activePage={activePage} onNodeSelected={navigateToPage} />
                    </div>
                    <Spinner visible={loading} />
                    <div className="PageCardList">{createPageCards()}</div>
                </Content>
                <Footer style={{position: 'fixed', right: '3rem', bottom: '3rem', padding: '0', background: 'none'}}>
                    <Row type="flex" justify="space-between">
                        <Col />
                        <Col>
                            <Button type="primary" shape="circle" icon="plus-circle" size="large" className="clickable" onClick={handleCreatePageClicked} />
                            <Modal title="Create new page" destroyOnClose={true} visible={createPageModalVisible} onCancel={handleCreatePageCancel} footer={null}>
                                <PageForm onOk={handleCreatePageOk} onCancel={handleCreatePageCancel} />
                            </Modal>
                        </Col>
                    </Row>
                </Footer>
            </Layout>
        </React.Fragment>
    );
};

const Spinner = props => {
    if (props.visible) {
        return (
            <div style={{ zIndex: 999, width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, backgroundColor: 'rgba(150, 150, 150, 0.5)', cursor: 'wait' }}>
                <Spin style={{ position: 'absolute', top: '50%', left: '50%' }} />
            </div>
        );
    }
    return '';
};

const mapStateToProps = state => ({
    page: state.page.page,
    pages: state.pages.pages,

    isCreating: state.pages.isCreating,
    createSuccess: state.pages.createSuccess,
    createError: state.pages.createError,

    isDeleting: state.pages.isDeleting,
    deleteSuccess: state.pages.deleteSuccess,
    deleteError: state.pages.deleteError,

    templates: state.templates.templates
});

const mapDispatchToProps = {
    createPage,
    fetchSinglePage,
    clearSinglePage
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Pages);
